import React, {
    useMemo,
    useState,
    useRef,
    useEffect,
    useCallback,
} from "react";
import ReportTable from "../../components/table/ReportTable";
import SectionHeading from "../../components/SectionHeading";
import ReportButtons from "../../components/ui/ReportButtons";
import sortIcon from "../../assets/icons/sort-icon.svg";
import filterIcon from "../../assets/icons/filter-icon.svg";
import Button from "../../components/ui/Button";
import ReactDOM from "react-dom";
import { Breadcrumb } from "../../components/ui/Breadcrumb";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import FooterNote from "../claimleaveview/FooterNote";
import moment from "moment";
import { useTableWorker } from "../../hooks/useTableWorker";
import ExportGraphToCsv from "./ExportGraphToCsv";
import { REPORTING_PERIOD, STATUS } from "../../constants";
import {
    getSelectedIsfOrganizationIds,
    getAllowedPoliciesAndReportingGroups,
} from "../../utils/helpers";
import { getFromCacheDB } from "../../utils/indexedDBCache";
import Loader from "../../components/ui/Loader";

const DetailedSnapshotReport = () => {
    const childRef = useRef(null);
    const location = useLocation();
    const tableDataRef = useRef(null);
    const exportRef = useRef();

    const { user } = useSelector((state) => state.auth);

    const {
        selectedOrganizationsReports,
        selectedOrganizationsIdReports: reportId,
        selectedRelatedOrganizationsForReports,
        selectedDataViewType: dataViewType,
        portalUserData: storedPortalUserData,
    } = useSelector((state) => state.reportOrganization);

    const { snapshotReportDetails } = useSelector(
        (state) => state.disabilitySnapshotData
    );

    // Get data from navigation state or fallback to Redux
    const navigationData = location.state;
    const selectedView = navigationData?.selectedView;
    const chartScreenshot = navigationData?.chartScreenshot;
    const disclaimer = navigationData?.disclaimer;
    const cacheKey = navigationData?.cacheKey;

    // State management
    const [selectedReportData, setSelectedReportData] = useState([]);
    const [data, setTableData] = useState([]);
    const [paginatedData, setPaginatedData] = useState([]);
    const [columnOrder, setColumnOrder] = useState([]);
    const [columnVisibility, setColumnVisibility] = useState({});
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [filteredDataLength, setFilteredDataLength] = useState(0);
    const [filteredData, setFilteredData] = useState([]);
    const [exportData, setExportData] = useState(false);
    const [pendingExport, setPendingExport] = useState(false);
    // Sorting state
    const [sorting, setSorting] = useState({
        id: null,
        desc: false,
        accessorKey: null,
        sortMethod: "",
    });

    // Filter state
    const [columnFilter, setColumnFilter] = useState([]);
    const [isPopupVisible, setPopupVisible] = useState(false);
    const [isPopupPosition, setIsPopupPosition] = useState({ top: 0, left: 0 });
    const [filterValue, setFilterValue] = useState(null);
    const [accessorKey, setAccessorKey] = useState("");
    const [columnName, setColumnName] = useState("");
    const [columnId, setColumnId] = useState("");

    const {
        processData,
        isLoading: isWorkerLoading,
        error: workerError,
    } = useTableWorker();

    // Process data with worker whenever sorting, filtering, or pagination changes
    useEffect(() => {
        const processTableData = async () => {
            if (!data || data.length === 0) {
                setPaginatedData([]);
                setFilteredDataLength(0);
                setTotalPages(0);
                setFilteredData([]);
                return;
            }
            let needFullData = false;
            if (exportData) {
                needFullData = true;              
            }
            try {
                const result = await processData(
                    data,
                    sorting.id ? [sorting] : [],
                    currentPage,
                    itemsPerPage,
                    columnFilter,
                    needFullData
                );
                
                setPaginatedData(result.paginatedData || []);
                setFilteredDataLength(result.totalItems || 0);
                setTotalPages(result.totalPages || 0);
                
                if (needFullData && result.filteredData) {
                    setFilteredData(result.filteredData || []);
                    setFilteredDataLength(result.totalItems || 0);
                    setExportData(false);
                    // Trigger export after data is ready
                    if (pendingExport) {
                        setPendingExport(false);
                        setTimeout(() => {
                            if (exportRef.current) {
                                exportRef.current.exportToExcel();
                            }
                        }, 0);
                    }
                } else if (!needFullData) {
                    setFilteredData(result.filteredData || []);
                }

                // If page was adjusted by worker, update current page
                if (result.pageAdjusted && result.currentPage !== currentPage) {
                    setCurrentPage(result.currentPage);
                }
            } catch (error) {
                console.error("Error processing table data:", error);
                setPaginatedData([]);
                setFilteredDataLength(0);
                setTotalPages(0);
                setFilteredData([]);
            }
        };

        processTableData();
    }, [data, sorting, currentPage, itemsPerPage, columnFilter, exportData, processData, pendingExport]);

    // Get columns from selectedView
    const columns = useMemo(() => {
        const columnInfo =
            selectedView?.TableDisplayInformation?.ColumnInformation || [];

        return columnInfo.map((col) => ({
            id: col.id,
            accessorKey: col.accessorKey,
            enableSorting: true,
            header: col.header,
            cell: ({ getValue }) => {
                const value = getValue();
                return value === null || value === undefined || value === ""
                    ? "—"
                    : value;
            },
            minSize: col?.minSize,
            maxSize: col?.maxSize,
            size: col?.size,
            columnId: col.columnId,
        }));
    }, [selectedView?.ViewId]);

    // Initialize column order and visibility when columns change
    useEffect(() => {
        if (columns.length > 0 && columnOrder.length === 0) {
            setColumnOrder(columns.map((col) => col.accessorKey));
            const initialVisibility = {};
            columns.forEach((col) => {
                initialVisibility[col.accessorKey] = true;
            });
            setColumnVisibility(initialVisibility);
        }
    }, [columns]);

    // Fetch cached data on mount
    useEffect(() => {
        const fetchCachedData = async () => {
            if (cacheKey) {
                const cachedData = await getFromCacheDB(cacheKey);
                if (cachedData) {
                    let exp = cachedData.chartData.filter(
                        (item) => item.ViewId === selectedView?.ViewId
                    );
                    setSelectedReportData(exp[0]);

                    // Store the table data in ref for future use
                    if (exp[0]?.tableInformation) {
                        tableDataRef.current = exp[0].tableInformation;
                        setTableData(exp[0].tableInformation);
                    } else {
                        console.warn(
                            "No tableInformation found in cached data"
                        );
                    }
                } else {
                    console.log(
                        "Cache miss - no data found for key:",
                        cacheKey
                    );
                }
            } else {
                console.log("No cache key provided in navigation state");
            }
        };

        fetchCachedData();
    }, [cacheKey, selectedView]);

    // Handlers
    const handlePageNumberClick = useCallback((pageNumber) => {
        setCurrentPage(pageNumber);
    }, []);

    const handleItemsPerPageChange = useCallback((newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1); // Reset to first page when changing items per page
    }, []);

    const handleColumnFilter = useCallback((filters) => {
        setColumnFilter(filters);
        setCurrentPage(1); // Reset to first page when filtering
    }, []);

    const handleColumnVisibility = useCallback((updater) => {
        setColumnVisibility((prev) => {
            if (typeof updater === "function") {
                return updater(prev);
            }
            return updater;
        });
    }, []);

    const getSortDetails = useCallback((sortDetails) => {
        setSorting({
            id: sortDetails.id,
            desc: sortDetails.desc,
            accessorKey: sortDetails.accessorKey,
            sortMethod: sortDetails.sortMethod || "default",
        });
        setCurrentPage(1); // Reset to first page when sorting
    }, []);

    const reorderColumn = useCallback(
        (draggedColumnId, targetColumnId, currentOrder) => {
            const draggedIndex = currentOrder.indexOf(draggedColumnId);
            const targetIndex = currentOrder.indexOf(targetColumnId);

            if (draggedIndex === -1 || targetIndex === -1) return currentOrder;

            const newOrder = [...currentOrder];
            newOrder.splice(draggedIndex, 1);
            newOrder.splice(targetIndex, 0, draggedColumnId);

            setColumnOrder(newOrder);
            return newOrder;
        },
        []
    );

    const closePopup = useCallback(() => setPopupVisible(false), []);

    const handleFilterChange = useCallback((e) => {
        e.preventDefault();
        const newValue = e.target.value;
        setFilterValue(newValue);
    }, []);

    const confirmFilter = useCallback(() => {
        if (filterValue && filterValue.trim()) {
            const newFilter = {
                columnName,
                accessorKey,
                filterValue: filterValue.trim(),
                id: accessorKey,
            };
            const existingFilterIndex = columnFilter.findIndex(
                (f) => f.accessorKey === accessorKey
            );
            if (existingFilterIndex > -1) {
                const updated = [...columnFilter];
                updated[existingFilterIndex] = newFilter;
                handleColumnFilter(updated);
            } else {
                handleColumnFilter([...columnFilter, newFilter]);
            }
        }
        setPopupVisible(false);
        setFilterValue("");
    }, [
        filterValue,
        accessorKey,
        columnName,
        columnFilter,
        handleColumnFilter,
    ]);

    const {
        relatedOrganizationNames,
        organizationName,
        selectedIsfOrganizationIds,
    } = getSelectedIsfOrganizationIds(
        selectedOrganizationsReports,
        reportId,
        selectedRelatedOrganizationsForReports
    );

    const { allowedPoliciesAndDivisions } = useMemo(() => {
        return getAllowedPoliciesAndReportingGroups(
            storedPortalUserData,
            selectedIsfOrganizationIds
        );
    }, [storedPortalUserData, selectedIsfOrganizationIds]);

    const initialPolicyDivision = useMemo(() => {
        return allowedPoliciesAndDivisions.flatMap((item) => [
            item.policyId,
            ...(Array.isArray(item.divisions)
                ? item.divisions.map(
                      (division) => `${item.policyId}-${division.divisionId}`
                  )
                : []),
        ]);
    }, [allowedPoliciesAndDivisions]);

    const selectedReportDetails = useMemo(() => {
        if (!snapshotReportDetails?.length) return {};
        return (
            snapshotReportDetails.find(
                (obj) =>
                    obj.DataViewType === "ClaimSnapshot" &&
                    obj.SubViewName === "Max Duration"
            ) || {}
        );
    }, [snapshotReportDetails]);

    const columnsInfo =
        selectedView?.TableDisplayInformation?.ColumnInformation;

    const reorderedColumns = useMemo(() => {
        return columnOrder
            .map((orderId) =>
                columnsInfo?.find((col) => col.accessorKey === orderId)
            )
            .filter(Boolean);
    }, [columnOrder, columnsInfo]);

    const reportParametersOptions =
        selectedReportDetails.ParametersOptions || [];
    const parameters = selectedReportDetails.Parameters || {};

    const getOptions = useMemo(() => {
        return (name) =>
            reportParametersOptions.find(
                (param) => param.parameterName === name
            )?.options || [];
    }, [reportParametersOptions]);

    const initializeFilterState = useMemo(() => {
        return (paramKey) =>
            parameters?.[paramKey]?.map((type) => ({
                name: type,
                value: type,
            })) || [];
    }, [parameters]);

    const reportingPeriodValue = useMemo(() => {
        return (
            getOptions(REPORTING_PERIOD).find(
                (option) => option.name === parameters?.reportingPeriod
            )?.value || []
        );
    }, [initializeFilterState, getOptions, parameters]);

    const claimCoverageTypes = useMemo(() => {
        return initializeFilterState("CoverageType").map((obj) => obj.value);
    }, [initializeFilterState]);

    const filterStatusValues = useMemo(() => {
        return initializeFilterState(STATUS).flatMap(
            (selected) =>
                getOptions(STATUS).find(
                    (option) => option.name === selected.name
                )?.value || []
        );
    }, [initializeFilterState, getOptions]);

    const handleExportData = useCallback((columnOrder) => {
        setColumnOrder(columnOrder);
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

    return (
        <>
            <Breadcrumb
                links={[
                    { name: "Dashboards", path: "/dashboard" },
                    ...(dataViewType
                        ? [
                              {
                                  name: dataViewType,
                                  path: "/dashboard/disability-snapshot",
                              },
                          ]
                        : []),
                    { name: selectedReportData?.title },
                ]}
            />
            <div className="section-card pt-0 px-0 pb-0">
                <div
                    className="sticky-filter-bar px-24p"
                    style={{ paddingTop: 24, borderRadius: 8 }}
                >
                    <div className="d-flex justify-content-between align-items-start">
                        <div className="d-flex justify-content-between align-items-start">
                            <SectionHeading
                                heading={selectedReportData?.title}
                            />
                        </div>
                        {filteredDataLength > 0 && (
                            <div className="data-view-button w-auto">
                                <ReportButtons
                                    label="Export data"
                                    onClick={() => {
                                        setPendingExport(true);
                                        setExportData(true);
                                    }}
                                    iconClass="fa-solid fa-download"
                                    ariaLabel="Export data"
                                    isDisabled={false}
                                    role="button"
                                />
                            </div>
                        )}
                    </div>

                    <p className="report-header pb-2">
                        {selectedReportData?.description}
                    </p>
                </div>

                {/* ReportTable Section */}
                <div style={{ padding: 0 }}>
                    <div>
                        {isWorkerLoading && paginatedData.length === 0 ? (
                            <Loader />
                        ) : (
                            <ReportTable
                                // Data and columns
                                data={paginatedData || []}
                                columns={columns || []}
                                // Table configuration
                                columnOrder={columnOrder}
                                setColumnOrder={setColumnOrder}
                                columnVisibility={columnVisibility}
                                handleColumnVisibility={handleColumnVisibility}
                                columnResizeMode="onChange"
                                // Sorting
                                sorting={sorting}
                                setSorting={setSorting}
                                getSortDetails={getSortDetails}
                                sortIcon={sortIcon}
                                manualSorting={true}
                                // Filtering
                                columnFilter={columnFilter}
                                handleColumnFilter={handleColumnFilter}
                                setPopupVisible={setPopupVisible}
                                setAccessorKey={setAccessorKey}
                                setColumnName={setColumnName}
                                setColumnId={setColumnId}
                                setIsPopupPosition={setIsPopupPosition}
                                setFilterValue={setFilterValue}
                                isPopupVisible={isPopupVisible}
                                columnName={columnName}
                                filterValue={filterValue}
                                // Column reordering
                                reorderColumn={reorderColumn}
                                setCheckColumnOrder={() => {}}
                                // Pagination
                                showPagination={true}
                                pageCount={totalPages || 1}
                                currentPage={currentPage}
                                handlePageNumberClick={handlePageNumberClick}
                                itemsPerPage={itemsPerPage}
                                handleItemsPerPageChange={
                                    handleItemsPerPageChange
                                }
                                filteredDataLength={filteredDataLength}
                                manualPagination={true}
                                // Loading states
                                isFetchingData={false}
                                isPaginating={isWorkerLoading}
                                isError={!!workerError}
                            />
                        )}

                        {isPopupVisible && ReactDOM.createPortal && (
                            <div
                                className="filter-popup-box section-card"
                                role="dialog"
                                aria-labelledby="filter-popup-title"
                                aria-describedby="filter-popup-description"
                                style={{
                                    top: isPopupPosition.top,
                                    left: isPopupPosition.left,
                                    zIndex: 1000,
                                }}
                                aria-modal="true"
                            >
                                <div className="top-line">
                                    <div
                                        className="title"
                                        id="filter-popup-title"
                                    >
                                        <img
                                            src={filterIcon}
                                            alt="Filter Icon"
                                        />
                                        <label>Filter {columnName}</label>
                                    </div>
                                    <div
                                        className="close-area"
                                        tabIndex={0}
                                        role="button"
                                        aria-label="Close filter popup"
                                        onClick={closePopup}
                                        onKeyDown={(e) => {
                                            if (
                                                e.key === "Enter" ||
                                                e.key === " "
                                            )
                                                closePopup();
                                        }}
                                    >
                                        <span className="close-text">
                                            Close
                                        </span>
                                    </div>
                                </div>
                                <div
                                    className="filter-box"
                                    id="filter-popup-description"
                                >
                                    <i
                                        className="fa fa-search search"
                                        aria-hidden="true"
                                    ></i>
                                    <input
                                        type="text"
                                        value={filterValue}
                                        onChange={handleFilterChange}
                                        placeholder="Filter"
                                        className="input-box"
                                        aria-label={`Filter input for ${columnName}`}
                                        autoFocus
                                    />
                                </div>
                                <div
                                    className="fs d-flex"
                                    style={{
                                        justifySelf: "end",
                                        width: "50%",
                                        marginTop: 16,
                                    }}
                                >
                                    <Button
                                        text="Apply"
                                        onClick={confirmFilter}
                                        disabled={!filterValue.trim()}
                                        aria-disabled={!filterValue.trim()}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {filteredDataLength > 0 && (
                <ExportGraphToCsv
                    ref={exportRef}
                    data={filteredData}
                    defaultColumns={reorderedColumns}
                    selectedReport={selectedView}
                    reportingPeriodValue={reportingPeriodValue}
                    onExport={handleExportData}
                    organizationName={organizationName}
                    relatedOrganizationNames={relatedOrganizationNames}
                    chartScreenshot={chartScreenshot}
                    reportLastFetched={moment().format("MM/DD/YYYY hh:mm A")}
                    user={user}
                    initialPolicyDivision={initialPolicyDivision}
                    claimCoverageTypes={claimCoverageTypes}
                    disclaimer={disclaimer}
                    filterStatusValues={filterStatusValues}
                    columnFilter={columnFilter}
                />
            )}

            <FooterNote
                date={moment().format("MM/DD/YYYY hh:mm A")}
                ref={childRef}
                reportDisclaimer="Data refreshed as of"
            />
        </>
    );
};

export default DetailedSnapshotReport;
